import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState, useContext, useRef } from "react"

import { PlusOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons"
import { Button, Col, Drawer, Form, Input, Row, Spin, Select, Radio, DatePicker, Flex, Typography, Tabs, Tag, TimeRangePickerProps } from "antd"
import { TabsProps } from "antd/lib"
import dayjs from "dayjs"

import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { SimpleModal, SimpleModalRef } from "@/components/_Shared/SimpleModal"
import { BulkOrderFuelTable } from "@/components/BulkOrder/BulkOrderFuel/BulkOrderFuelTable"
import { BulkOrderSummary } from "@/components/BulkOrder/BulkOrderSummary/BulkOrderSummary"
import { ShippingScheduleTable } from "@/components/BulkOrder/ShippingSchedule/ShippingScheduleTable"
import { Resource, Scope } from "@/constants/permission"
import { CustomerContext } from "@/context/CustomerContext"
import { getCustomerClientsSelectList } from "@/http/customerClient"
import { getNotArchivedFuelTypesByDepot } from "@/http/fuelType"

import classes from "./BulkOrderAddForm.module.scss"

import { useBulkOrderMutation } from "@/hook/BulkOrders/useBulkOrderMutation"
import { useBulkOrderByIdQuery } from "@/hook/BulkOrders/useBulkOrderQuery"
import { usePersonalOfferMutation } from "@/hook/BulkOrders/usePersonalOfferMutation"
import { useCustomerByIdQuery } from "@/hook/Dictionaries/Customers/useCustomerQuery"
import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { usePermission } from "@/hook/usePermission"

import { IBulkOrder, IBulkOrderCreate, IBulkOrderFuel, IBulkOrderFuelCreate, IBulkOrderUpdate, IShippingSchedule } from "@/types/IBulkOrder"
import { IFuelType } from "@/types/IFuelType"
import { DeliveryType } from "@/types/IOrder"
import { ISelectListItem, ITableColumn } from "@/types/ui"

import { filterSelectOption } from "@/utils/filterSelectOption"
import { formatDateTime, onlyDateFormat } from "@/utils/formatDate"

const { RangePicker } = DatePicker
const { Text } = Typography

export interface BulkOrderAddFormProps {
    open(id?: number, isPersonalOffer?: boolean): void
    close(): void
}

interface  BulkOrderFormValues {
    rangeDate: string[]
	deliveryType: DeliveryType
	customerId: number
	depotId: number
    clientIds: number[]
}

interface  BulkOrderFuelFormValues {
    fuelTypeId: number
    quantity: number
    offeredPrice?: number
    fuels: IBulkOrderFuel[]
}

interface  SheduleFormValues extends BulkOrderFormValues {
    fuels: IShippingSchedule[]
}

export const BulkOrderAddForm = forwardRef<BulkOrderAddFormProps>((_props, ref) => {
    const { hasPermission } = usePermission()
    const [form] = Form.useForm<BulkOrderFormValues>()
    const [formFuels] = Form.useForm<BulkOrderFuelFormValues>()
    const [formShedule] = Form.useForm<SheduleFormValues>()
    
    const [id, setId] = useState<number>(0)
    const [isOpen, setIsOpen] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const [isAdmin, setIsAdmin] = useState<boolean>(hasPermission(Resource.RequestQuotes, Scope.AllActions))
    const [fuelTypes, setFuelTypes] = useState<IFuelType[]>()
    const [fuels, setFuels] = useState<IBulkOrderFuelCreate[]>([])
    const [isShowDescriptionRangeDate, setIsShowDescriptionRangeDate] = useState<boolean>(false)
    const [descriptionRangeDate, setDescriptionRangeDate] = useState<string>("")
    const [activeKey, setActiveKey] = useState("1")
    const [isSheduleValidate, setIssheduleValidate] = useState<boolean>(false)
    const [isPersonalOffer, setIsPersonalOffer] = useState<boolean>(false)
    const [clients, setClients] = useState<ISelectListItem[]>([])
    const [focusGuid, setFocusGuid] = useState<string>("")
    const [focusName, setFocusName] = useState<string>("")

    const { data: bulkOrder } = useBulkOrderByIdQuery(id)
    const { createMutation, updateMutation } = useBulkOrderMutation()
    const { 
        createMutation: personalOfferCreateMutation,
        updateMutation: personalOfferUpdateMutation } = usePersonalOfferMutation()
    const { data: depots, isLoading: depotsIsLoading } = useDepotSelectListQuery()

    const customer = useCustomerByIdQuery(useContext(CustomerContext).customer?.id ?? 0)
    const simpleModalComponent = useRef<SimpleModalRef>(null)

    useImperativeHandle(ref, () => ({
        open(id?: number, isPersonalOffer?: boolean) {
            onOpenHandler(id, isPersonalOffer)
        },
        close() {
            onCloseHandler()
        }
    }))

    useEffect(() => {
        if (bulkOrder && isOpen) {
            const response = bulkOrder.response
            
            form.setFields([
                {
                    name: "rangeDate",
                    value: [dayjs(response.startDate), dayjs(response.endDate)]
                },
                {
                    name: "deliveryType",
                    value: response.deliveryType
                },
                {
                    name: "depotId",
                    value: response.depotId 
                }
            ])

            setFuels(
                response.fuels.map((t) => ({
                    guid: crypto.randomUUID(),
                    id: t.id,
                    fuelTypeId: t.fuel.fuelType.id,
                    fuelTypeName: t.fuel.fuelType.name ?? "-",
                    quantity: t.quantity,
                    offeredPrice: t.offeredPrice,
                    fuel: t.fuel,
                    shippingSchedules: t.shippingSchedules?.map((x) => ({
                        guid: crypto.randomUUID(),
                        id: x.id,
                        quantity: x.quantity,
                        price: x.price,
                        startDate: x.startDate,
                        endDate: x.endDate,
                        isTransportSelect: x.isTransportSelect,
                        fuelTypeName: x.fuelTypeName
                    }))
                }))
            )

            uploadFuelTypes(response.depotId, undefined)
            onChangeRangeDateHandler()
        }
    }, [id, bulkOrder, form, isOpen])

    const fuelTableAdditionalColumns: ITableColumn<IBulkOrderFuelCreate>[] = [{
        title: "Удалить",
        dataIndex: "delete",
        key: "delete",
        isVisible: true,
        order: 4,
        render: (_, { guid }) =>
        <>
            <Flex vertical={true} align="center">
                <ColorifiedButton
                    themecolor="orange"       
                    type={"primary"}
                    size={"small"}
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={() => onDeleteBulkOrderFuelHandler(guid as string)}
                />
            </Flex>
        </>
    }]

    const shippingScheduleTableAdditionalColumns: ITableColumn<IShippingSchedule>[] = [{
        title: "Удалить",
        dataIndex: "delete",
        key: "delete",
        isVisible: true,
        order: 4,
        render: (_, { guid }) =>
        <>
            <Flex vertical={true} align="center">
                <ColorifiedButton
                    themecolor="orange"       
                    type={"primary"}
                    size={"small"}
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={() => onDeleteShippingScheduleHandler(guid as string)}
                />
            </Flex>
        </>
    }]

    const onFinish = async (data: BulkOrderFormValues) => {
        try {
            setIsProcessing(true)
            const startDateDayjs = form.getFieldValue("rangeDate")[0]
            const endDateDayjs = form.getFieldValue("rangeDate")[1]

            const startDateStr = startDateDayjs ? startDateDayjs.format("YYYY-MM-DD") : undefined
            const endDateStr = endDateDayjs ? endDateDayjs.format("YYYY-MM-DD") : undefined
            const clientIds = form.getFieldValue("clientIds") as number[]

            const request: IBulkOrderCreate = {
               startDate: startDateStr as string,
               endDate: endDateStr as string,
               deliveryType: data.deliveryType,
               customerIds: isPersonalOffer
               ? []
               : [customer.data?.response?.id ? customer.data?.response.id : 0],
               customerClientIds: isPersonalOffer ? clientIds : undefined,
               depotId: data.depotId,
               isPersonalOffer: isPersonalOffer,
               fuels: fuels.map((t) => ({
                    id: t.id,
                    fuelTypeId: t.fuelTypeId,
                    fuelTypeName: t.fuelTypeName,
                    quantity: t.quantity,
                    offeredPrice: t.offeredPrice,
                        shippingSchedules: isAdmin && !isPersonalOffer ? t.shippingSchedules : [{
                        guid: crypto.randomUUID(),
                        startDate: isPersonalOffer ? startDateStr as string : new Date(),
                        endDate: isPersonalOffer ? endDateStr as string :  new Date(),
                        quantity: t.quantity,
                        price: t.offeredPrice
                    }]
                }))
            }

            if (id) {
                if(isPersonalOffer){
                    await personalOfferUpdateMutation.mutateAsync({
                    ...request,
                    id: id
                })
                }
                else{
                    await updateMutation.mutateAsync({
                    ...request,
                    id: id
                })
                }
            } else {
                if(isPersonalOffer){
                    await personalOfferCreateMutation.mutateAsync(request)
                }
                else {
                    await createMutation.mutateAsync(request)
                }
            }

            form.resetFields()
            onCloseHandler()
        } finally {
            setIsProcessing(false)
        }
    }
    
    const title = useMemo(() => {
        if (id) {
            const description = `№ ${id} от ${formatDateTime(bulkOrder?.response.createdOn as string, onlyDateFormat)}${isAdmin ? ` ${bulkOrder?.response.customerName}`: ""}`
            return `${isPersonalOffer ? "Персональное предложение" : "Запрос предложения"} ${description}`
        }

        return isPersonalOffer ? "Персональное предложение" : "Запрос предложения"
    }, [id, bulkOrder, isPersonalOffer])

    const onOpenHandler = (id?: number, isPersonalOffer?: boolean) => {
        form.resetFields()
        setIsOpen(true)

        if (id) {
            setId(id)
        }
        else{
            form.setFieldValue("deliveryType", DeliveryType.Pickup)
        }

        if(isPersonalOffer){
            setIsPersonalOffer(isPersonalOffer)

            getCustomerClientsSelectList().then((response) => {
                setClients(response?.response ?? [])
            })
        }
    }

    const onCloseHandler = () => {
        setId(0)
        form.resetFields()
        formFuels.resetFields()
        formShedule.resetFields()
        setFuels([])
        setFuelTypes([])
        setIsShowDescriptionRangeDate(false)
        setIsOpen(false)
        setActiveKey("1")
        setIsPersonalOffer(false)
        setClients([])
    }

    const onAddFuelHandler = async () => {
        try {
            await formFuels.validateFields()
        } catch (e) {
            return
        }

        const quantity = formFuels.getFieldValue("quantity")
        const fuelTypeId = formFuels.getFieldValue("fuelTypeId")
        let offeredPrice = formFuels.getFieldValue("offeredPrice")
        if (offeredPrice === "") {
            offeredPrice = undefined
        }

        const fuel = fuels.find(x => x.fuelTypeId === fuelTypeId)
        if (fuel) {
            onWarningFuelTypeBulkOrder(fuel, quantity, offeredPrice)
            return
        }

        fuels.push({
            guid: crypto.randomUUID(),
            fuelTypeId: fuelTypeId,
            fuelTypeName: fuelTypes?.find(x => x.id === fuelTypeId)?.name ?? "-",
            quantity: quantity,
            offeredPrice: offeredPrice,
            shippingSchedules: [{
                guid: crypto.randomUUID(),
                startDate: new Date(),
                endDate: new Date(),
                quantity: quantity,
                price: offeredPrice
            }]
        })
       
        refreshFuels()
        formFuels.resetFields()
    }

    const onDepotChange = (isFuelTypeSet?: boolean) => {
        if (depots) {
            const depot = depots.response.find(
                (d) => d.id === form.getFieldValue("depotId")
            )

            if (depot) {
                uploadFuelTypes(depot.id, isFuelTypeSet)
                setFuels([])
            }
        }
    }

    const uploadFuelTypes = (depotId: number, _isFuelTypeSet?: boolean) => {
        if (depotId) {
            getNotArchivedFuelTypesByDepot(depotId).then(res => {
                setFuelTypes(res.response ?? null)
            })
        }
    }
    
    const onChangeRangeDateHandler = () => {
        if (form.getFieldValue("rangeDate")?.length === 2) {
            setIsShowDescriptionRangeDate(true)
            setDescriptionRangeDate(`Примечание: период планируемой отгрузки 
                                    с ${form.getFieldValue("rangeDate")[0].format("DD-MM-YYYY")} 00:00 
                                    по ${form.getFieldValue("rangeDate")[1].format("DD-MM-YYYY")} 23:59`)
        } else {
            setIsShowDescriptionRangeDate(false)
            setDescriptionRangeDate("")
        }
    }

    const onDeleteBulkOrderFuelHandler = (guid: string) => {
        setFuels(
            fuels.filter(x => x.guid !== guid).map((t) => ({
                guid: t.guid,
                id: t.id,
                fuelTypeId: t.fuelTypeId,
                fuelTypeName: t.fuelTypeName,
                quantity: t.quantity,
                offeredPrice: t.offeredPrice,
                shippingSchedules: t.shippingSchedules
        })))
    }

    const disabledDate: TimeRangePickerProps["disabledDate"] = (current) => {
        return current && current < dayjs().add(-1, "day")
    }

    const onСhangeBulkOrderFuelTable = (guid: string, name: string, value: string) => {
        const fuel = fuels.find(x => x.guid === guid)

        if (!fuel) {
            return
        }

        switch(name) {
            case "fuelType":
               fuel.fuelTypeId = Number(value)
               break
            case "quantity":
               fuel.quantity = Number(value)
               break
            case "offeredPrice":
               fuel.offeredPrice = value ? Number(value) : undefined
               break
        }
        
        if (isAdmin) {
            setFocusGuid(guid)
            setFocusName(name)

            setFuels(
                fuels.map((t) => ({
                    guid: t.guid,                
                    id: t.id,
                    fuelTypeId: t.fuelTypeId,
                    fuelTypeName: t.fuelTypeName,
                    quantity: t.quantity,
                    offeredPrice: t.offeredPrice,
                    fuel: t.fuel,
                    shippingSchedules: isAdmin && !isPersonalOffer ? t.shippingSchedules : [{
                        guid: crypto.randomUUID(),
                        startDate: new Date(),
                        endDate: new Date(),
                        quantity: t.quantity,
                        price: t.offeredPrice
                    }]
                }))
            )
        }
    }

    useEffect(() => {
        const names = document.querySelectorAll(`[name="${focusName}"]`) as NodeListOf<HTMLAnchorElement>
        const arrayOfNodes = Array.from(names).find(x => x.id === focusGuid)
        arrayOfNodes?.focus()
    }, [fuels])

    const refreshFuels = () => {
        setFuels(x => [...x])
    }

    const refreshFuelByShippingSchedule = (fuel: IBulkOrderFuelCreate) => {
        if (!fuel.shippingSchedules) {
            fuel.quantity = 0
            fuel.offeredPrice = undefined
            return
        }

        fuel.quantity = fuel.shippingSchedules.reduce((acc, num) => acc + num.quantity, 0)
        fuel.offeredPrice = fuel.shippingSchedules.reduce((acc, num) => acc + (num.price ?? 0), 0)
        if (fuel.offeredPrice === 0) {
            fuel.offeredPrice = undefined
        }

        const startDates = fuels.flatMap(x => x.shippingSchedules).map(x => (x as IShippingSchedule).startDate)
        const endDates = fuels.flatMap(x => x.shippingSchedules).map(x => (x as IShippingSchedule).endDate)

        if (startDates.some(x => x) && endDates.some(x => x)) {
            const minDate = new Date(startDates.reduce((min, v) => min <= (new Date(v)).getTime() ? min : (new Date(v)).getTime(), Infinity))
            const maxDate = new Date(endDates.reduce((max, v) => max >= (new Date(v)).getTime() ? max : (new Date(v)).getTime(), 0))
            
            form.setFields([
                {
                    name: "rangeDate",
                    value: [dayjs(minDate), dayjs(maxDate)]
                }
            ])
        }
    }

    const onAddShippingScheduleHandler = (guid: string) => {
        const bulkOrderFuel = fuels.find(x => x.guid === guid)

        if (!bulkOrderFuel || !bulkOrderFuel.shippingSchedules) {
            return
        }

        const startDate = form.getFieldValue("rangeDate")[0]
        const endDate = form.getFieldValue("rangeDate")[1]
        if (!startDate || !endDate) {
            return
        }

        const startDateStr = startDate.format("YYYY-MM-DD")
        const endDateStr = endDate.format("YYYY-MM-DD")

        const last = bulkOrderFuel.shippingSchedules.at(-1)

        bulkOrderFuel.shippingSchedules.push({
            guid: crypto.randomUUID(),
            quantity: last ? last.quantity : 0,
            price: last?.price,
            startDate: startDateStr,
            endDate: endDateStr
        })

        refreshFuelByShippingSchedule(bulkOrderFuel)
        refreshFuels()
    }

    const onDeleteShippingScheduleHandler = (guid: string) => {

        const fuel = fuels.find(x => x.shippingSchedules?.map(y => y.guid).includes(guid))
        if (!fuel) {
            return
        }

        fuel.shippingSchedules = fuel.shippingSchedules?.filter(x => x.guid !== guid)

        refreshFuelByShippingSchedule(fuel)
        refreshFuels()
    }

    const onChangeShippingScheduleHandler = (bulkOrderFuelGuid: string, shippingScheduleGuid: string, name: string, value: string) => {
        const fuel = fuels.find(x => x.guid === bulkOrderFuelGuid)
        if (!fuel) {
            return
        }

        const schedule = fuel.shippingSchedules?.find(x => x.guid === shippingScheduleGuid)
        if (!schedule) {
            return
        }

        switch(name) {           
            case `period_${bulkOrderFuelGuid}`:
                schedule.startDate = new Date(value[0])
                schedule.endDate = new Date(value[1])
                break
            case `quantity_${bulkOrderFuelGuid}`:
                schedule.quantity = Number(value)
               
                break
            case `price_${bulkOrderFuelGuid}`:
                schedule.price = value ? Number(value) : undefined
                break
        }

        setFocusGuid(shippingScheduleGuid)
        setFocusName(name)
        refreshFuelByShippingSchedule(fuel)
        refreshFuels()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const bulkOrderForm = 
        <>
             <Form layout="vertical" form={form} onFinish={onFinish}>
                <Spin spinning={isProcessing}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                hidden={!isPersonalOffer || id !== 0}
                                name="clientIds"
                                label="Клиенты"
                                rules={[
                                  {
                                    required: isPersonalOffer && id === 0,
                                    message: "Пожалуйста выберите клиентов"
                                  }
                                ]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Клиенты"
                                    showSearch
                                    filterOption={(inputValue, option) =>
                                        filterSelectOption(inputValue, option?.label || "")
                                    }
                                    options={clients}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="rangeDate"
                                label="Период планируемой отгрузки"
                                style={{ marginBottom: 0 }}
                                rules={[
                                    {
                                        required: true,
                                        message: "Пожалуйста, введите период планируемой отгрузки"
                                    }
                                ]}
                            >                                
                                <RangePicker 
                                    style={{ width: "100%" }}
                                    format={"DD-MM-YYYY"}
                                    disabledDate={disabledDate}
                                    onChange={onChangeRangeDateHandler}
                                />
                            </Form.Item>
                            {isShowDescriptionRangeDate &&
                                <Text>{descriptionRangeDate}</Text>
                            }
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="deliveryType"
                                label=" "
                            >
                                <Radio.Group defaultValue={DeliveryType.Pickup}>
                                    <Radio.Button value={DeliveryType.Pickup} checked={true}>Самовывоз</Radio.Button>
                                    <Radio.Button value={DeliveryType.Delivery}>Доставка</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item 
                                name={"depotId"}
                                label= "Нефтебаза"
                                rules={[ {
                                    required : true,
                                    message : "Пожалуйста выберите нефтебазу"
                                }
                            ]}>
                                <Select 
                                    placeholder = "Нефтебаза"
                                    showSearch
                                    onChange={() => { onDepotChange()}}
                                    options={depots?.response.map((depot) => ({
                                        value: depot.id,
                                        label: depot.name
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Spin>
            </Form>
            <Form layout="vertical" form={formFuels}>
                <Row gutter={16} align="middle">                        
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="fuelTypeId"
                            label="Тип топлива"
                            rules={[
                            {
                            required : true,
                            message : "Пожалуйста выберите тип топлива"
                            }
                        ]}>
                            <Select
                            placeholder="Тип топлива"
                            showSearch
                            options={fuelTypes?.map((fuelType) => ({
                                value: fuelType.id,
                                label: `${fuelType.name}`
                            }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item
                            name="quantity"
                            label="Количество, тн"
                            rules={[
                                {
                                    required: true,
                                    message: "Введите количество в тн"
                                }
                            ]}
                        >
                            <Input/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item
                            name="offeredPrice"
                            label="Предложить свою цену"
                            rules={[
                                {
                                    required: true,
                                    message: "Введите цену"
                                }
                            ]}
                        >
                            <Input placeholder="на дату отгрузки"/>
                        </Form.Item>
                    </Col>

                </Row>
                <ColorifiedButton
                    themecolor="blue"       
                    type={"primary"}
                    size={"middle"}
                    icon={<PlusOutlined />}
                    onClick={onAddFuelHandler}
                >
                    Добавить топливо 
                </ColorifiedButton>
                <Row>
                    <Col>
                        <Text>Добавленное топливо:</Text>
                    </Col>
                </Row>
                <div>
                    <BulkOrderFuelTable 
                        fuels={fuels ?? []}
                        fuelTypes={fuelTypes}
                        additionalColumns={fuelTableAdditionalColumns}
                        onChange={onСhangeBulkOrderFuelTable}
                    />
                </div>
                {
                    isAdmin && bulkOrder &&
                    <>
                        <BulkOrderSummary
                            fuels={fuels}
                            logs={bulkOrder.response.logs}
                        />
                    </>

                }
            </Form>
        </>

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const sheduleForm = (
        <Flex vertical={true}>
            {
                fuels && fuels.map(x => (
                    <ShippingScheduleTable
                        bulkOrder={bulkOrder?.response as IBulkOrder}
                        bulkOrderFuel={x}
                        onAdd={onAddShippingScheduleHandler}
                        onChange={onChangeShippingScheduleHandler}
                        additionalColumns={shippingScheduleTableAdditionalColumns}
                    />
                ))
            }
            {bulkOrder &&
                <BulkOrderSummary
                    fuels={fuels}
                    logs={bulkOrder.response.logs}
                />
            }
        </Flex>
    )

    const onWarningFuelTypeBulkOrder = async (fuel: IBulkOrderFuelCreate, quantity: number, offeredPrice: number | undefined) => {
        simpleModalComponent.current?.open({
        title: "Предупреждение",
        text: "Выбранный тип топлива уже добавлен. Обновить данные по топливу?",
        ok: {
            text: "Обновить",            
            action: async () => {
            fuel.quantity = quantity
            fuel.offeredPrice = offeredPrice
            refreshFuels()
            formFuels.resetFields()
            simpleModalComponent.current?.close()
            }
        },
        cancel: {
            text: "Выбрать другой тип топлива",
            action: () => {
                simpleModalComponent.current?.close()
            }
        }
        })
    }
    
    return (
        <>
            <SimpleModal ref={simpleModalComponent}/>
            <Drawer
                title={title}
                width={820}
                onClose={onCloseHandler}
                open={isOpen}
                styles={{ body: { paddingBottom: 80 } }}
                extra={
                    <Button type="primary" disabled={isProcessing} onClick={form.submit}>
                        {isPersonalOffer ? "Отправить предложение" : "Отправить на согласование"}
                    </Button>
                }
            >
                {
                    isAdmin && !isPersonalOffer ? (
                        <>
                            <div hidden>
                                {bulkOrderForm}
                            </div>
                            {sheduleForm}
                        </>
                    ) : bulkOrderForm
                }
            </Drawer>
        </>

    )
})